import { DOCUMENT } from '@angular/common';
import { Injectable, effect, inject, signal } from '@angular/core';
import { Storage } from '@tvd/utils/storage';

export type ThemePreference = 'light' | 'dark' | 'system';

/**
 * The Theme Preference is what the user chose; the rendered (effective) theme
 * is resolved by CSS alone. `system` means no `data-theme` attribute and no
 * storage key — bare `:root` has `color-scheme: light dark`, so the browser
 * follows the OS live. JS never computes the effective theme.
 * See docs/adr/0006-css-native-theme-resolution.md.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  readonly #document = inject(DOCUMENT);
  readonly #storage = inject(Storage);

  readonly #preference = signal<ThemePreference>(
    this.#storage.get('THEME_PREFERENCE') ?? 'system',
  );
  readonly preference = this.#preference.asReadonly();

  constructor() {
    effect(() => {
      this.#applyPreference();
    });
  }

  setPreference(preference: ThemePreference): void {
    this.#preference.set(preference);
    if (preference === 'system') {
      this.#storage.delete('THEME_PREFERENCE');
    } else {
      this.#storage.set('THEME_PREFERENCE', preference);
    }
  }

  #applyPreference(): void {
    const root = this.#document.documentElement;
    const preference = this.#preference();
    if (preference === 'system') {
      root.removeAttribute('data-theme');
    } else {
      root.setAttribute('data-theme', preference);
    }
  }
}
